/**
 * PURE — what the file already says about a metric, for the custom-metric form.
 *
 * A customer file repeats each metric's definition on every row: name, unit,
 * format, scale, direction, description. The form used to ignore all of it and
 * start blank, so a 49-metric file meant retyping 49 definitions — and the
 * defaults it fell back to (scale "0-100", "higher is better") silently
 * disagreed with the file, which QC then flagged as the file's fault.
 *
 * Category is the one field handled conservatively. The file's Category ID
 * only pre-selects a category when it equals one of the organization's keys
 * exactly; failing that, the Category text must equal exactly one category's
 * label or long label. Anything looser is left for the user to choose — a
 * metric filed under the wrong category scores in the wrong place, and nothing
 * downstream would say so.
 */
import { HEADER_ALIASES, normalizeHeader } from "./header-aliases";
import type { CategoryOption } from "../data/api";

export type CustomMetricSeed = {
  metric_id: string;
  metric_name?: string;
  /** The file's Category ID, verbatim. Shown to the user; never trusted blindly. */
  category_id?: string;
  /** The file's Category text, verbatim. */
  category_label?: string;
  subcategory?: string;
  unit_of_measure?: string;
  format?: string;
  scale?: string;
  normalization_type?: string;
  normalization_direction?: string;
  description?: string;
  business_impact?: string;
  formula?: string;
  source_system?: string;
  kpi_status?: string;
};

/**
 * Columns the importer does not accept (so they have no mapping target) but
 * that still describe the metric. Read straight from the raw header.
 */
const EXTRA_ALIASES: Record<string, readonly string[]> = {
  category_id: [
    "category_id",
    "category_ids",
    "cat_id",
    "category_code",
    "category_slug",
  ],
  normalization_type: [
    "normalization_type",
    "normalisation_type",
    "normalization_technique",
    "norm_type",
  ],
  normalization_direction: [
    "normalization_direction",
    "normalisation_direction",
    "norm_direction",
    "direction",
  ],
};

type Row = Record<string, string>;

/**
 * The first non-blank value for `field` in `row`: the mapped column first,
 * then any raw header that is a known spelling of it. The fallback matters
 * because a user may set a duplicated column to "Do not import" and still
 * expect its value to describe the metric.
 */
function valueFor(
  row: Row,
  headers: string[],
  field: string,
  mappedHeader: string | null,
  aliases: readonly string[]
): string | undefined {
  const pick = (h: string | null | undefined) => {
    const v = h ? (row[h] ?? "").trim() : "";
    return v || undefined;
  };
  const mapped = pick(mappedHeader);
  if (mapped) return mapped;
  const wanted = new Set([field, ...aliases].map(normalizeHeader));
  for (const h of headers) {
    if (wanted.has(normalizeHeader(h))) {
      const v = pick(h);
      if (v) return v;
    }
  }
  return undefined;
}

/**
 * Build the seed for one metric from the first row that carries it.
 *
 * `headerFor` is the Columns step's mapping (canonical field -> file header).
 */
export function seedFromFile(
  metricValue: string,
  rows: Row[],
  headers: string[],
  metricHeader: string | null,
  headerFor: (field: string) => string | null
): CustomMetricSeed {
  const seed: CustomMetricSeed = { metric_id: metricValue };
  if (!metricHeader) return seed;
  const row = rows.find(r => (r[metricHeader] ?? "").trim() === metricValue);
  if (!row) return seed;

  const canonical = (field: keyof typeof HEADER_ALIASES) =>
    valueFor(row, headers, field, headerFor(field), HEADER_ALIASES[field]);
  const extra = (field: keyof typeof EXTRA_ALIASES) =>
    valueFor(row, headers, field, null, EXTRA_ALIASES[field]);

  seed.metric_name = canonical("metric_name");
  seed.category_label = canonical("category");
  seed.subcategory = canonical("subcategory");
  seed.unit_of_measure = canonical("unit_of_measure");
  seed.format = canonical("format");
  seed.scale = canonical("scale");
  seed.description = canonical("description");
  seed.business_impact = canonical("business_impact");
  seed.formula = canonical("formula_compute");
  seed.source_system = canonical("source_system");
  seed.kpi_status = canonical("kpi_status");
  seed.category_id = extra("category_id");
  seed.normalization_type = extra("normalization_type");
  seed.normalization_direction = extra("normalization_direction");
  return seed;
}

/**
 * The category to pre-select, or null when the file does not identify exactly
 * one. See the module comment for why this is exact-only.
 */
export function preselectCategory(
  seed: Pick<CustomMetricSeed, "category_id" | "category_label">,
  options: CategoryOption[]
): string | null {
  const id = (seed.category_id ?? "").trim().toLowerCase();
  if (id) {
    const byKey = options.filter(o => o.key.toLowerCase() === id);
    if (byKey.length === 1) return byKey[0].key;
  }
  const label = (seed.category_label ?? "").trim().toLowerCase();
  if (label) {
    const byLabel = options.filter(
      o =>
        o.label.trim().toLowerCase() === label ||
        (o.long_label ?? "").trim().toLowerCase() === label
    );
    if (byLabel.length === 1) return byLabel[0].key;
  }
  return null;
}

/** "higher_better" / "Lower is better" / "lower" -> the server's two values. */
export function directionFrom(raw: string | undefined): string | null {
  const v = (raw ?? "")
    .trim()
    .toLowerCase()
    .replace(/[\s-]+/g, "_");
  if (!v) return null;
  if (v.startsWith("higher") || v === "up" || v === "positive")
    return "higher_better";
  if (v.startsWith("lower") || v === "down" || v === "negative")
    return "lower_better";
  return null;
}

/**
 * Always offered, on top of whatever the catalog already uses. Spelled the
 * way the catalog and customer files spell them ("0 to 100+", "Percentage"),
 * because QC compares a row's scale and format to its definition as text.
 */
export const DEFAULT_SCALES: readonly string[] = [
  "0 to 1",
  "0 to 10",
  "0 to 100",
  "0 to 100+",
  "1 to 5",
  "1 to 10",
  "-100 to +100",
];
export const DEFAULT_FORMATS: readonly string[] = [
  "Numeric",
  "Integer",
  "Decimal",
  "Percentage",
  "Currency",
  "Boolean",
];

/** Distinct non-blank values, catalog order kept, extras appended. */
export function mergeChoices(
  fromCatalog: (string | null | undefined)[],
  extras: readonly string[]
): string[] {
  const out: string[] = [];
  const seen = new Set<string>();
  [...fromCatalog, ...extras].forEach(v => {
    const t = (v ?? "").trim();
    if (t && !seen.has(t.toLowerCase())) {
      seen.add(t.toLowerCase());
      out.push(t);
    }
  });
  return out;
}
